import React from 'react';
import type { Automation } from '../data/automationLibrary';

interface AutomationDetailProps {
  automation: Automation;
  onBack: () => void;
}

const AutomationDetail: React.FC<AutomationDetailProps> = ({ automation, onBack }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#22c55e';
      case 'paused': return '#f59e0b';
      case 'development': return '#3b82f6';
      case 'error': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getLogIcon = (type: string) => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'info': return 'ℹ️';
      default: return '📝';
    }
  };

  const getBrandEmoji = (brand: string) => {
    switch (brand) {
      case 'mars-men':
        return '🚀';
      case 'hollow-socks':
        return '🧦';
      case 'easy-street':
        return '🛣️';
      default:
        return '⚙️';
    }
  };

  const formatBrandLabel = (brand: string) => {
    switch (brand) {
      case 'mars-men':
        return 'Mars Men';
      case 'hollow-socks':
        return 'Hollow Socks';
      case 'easy-street':
        return 'Easy Street';
      default:
        return brand.replace('-', ' ');
    }
  };

  return (
    <div className="automation-detail">
      <button onClick={onBack} className="back-button">
        ← Back to Library
      </button>

      <div className="detail-header">
        <div className="detail-title-section">
          <h2>{automation.name}</h2>
          <span 
            className="status-badge"
            style={{ 
              backgroundColor: `${getStatusColor(automation.status)}20`,
              color: getStatusColor(automation.status),
              border: `1px solid ${getStatusColor(automation.status)}40`
            }}
          >
            {automation.status.toUpperCase()}
          </span>
          <span className="version-badge">v{automation.version}</span>
        </div>
        <p className="detail-description">{automation.description}</p>
      </div>

      <div className="detail-grid">
        <div className="detail-main">
          <div className="detail-card">
            <h3>Configuration</h3>
            <div className="config-section">
              <h4>Triggers</h4>
              <ul className="config-list">
                {automation.triggers.map((trigger, index) => (
                  <li key={index}>
                    <span className="trigger-icon">⚡</span>
                    {trigger}
                  </li>
                ))}
              </ul>
            </div>
            <div className="config-section">
              <h4>Actions</h4>
              <ol className="action-list">
                {automation.actions.map((action, index) => (
                  <li key={index}>
                    <span className="action-number">{index + 1}</span>
                    {action}
                  </li>
                ))}
              </ol>
            </div>
            <div className="config-section">
              <h4>Supported Brands</h4>
              <div className="brand-tags">
                {automation.brands.map((brand, index) => (
                  <span key={index} className="brand-tag">
                    {getBrandEmoji(brand)} {formatBrandLabel(brand)}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {automation.logs && (
            <div className="detail-card">
              <h3>Recent Activity</h3>
              <div className="logs-container">
                {automation.logs.map((log, index) => (
                  <div key={index} className={`log-entry log-${log.type}`}>
                    <span className="log-icon">{getLogIcon(log.type)}</span>
                    <div className="log-content">
                      <span className="log-message">{log.message}</span>
                      <span className="log-time">{formatDate(log.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="detail-sidebar">
          {automation.metrics && (
            <div className="detail-card metrics-card">
              <h3>Performance Metrics</h3>
              <div className="metric-grid">
                <div className="metric-item">
                  <span className="metric-value">{automation.metrics.runsToday}</span>
                  <span className="metric-label">Runs Today</span>
                </div>
                <div className="metric-item">
                  <span className="metric-value">{automation.metrics.runsThisWeek}</span>
                  <span className="metric-label">This Week</span>
                </div>
                <div className="metric-item">
                  <span className="metric-value">{automation.metrics.successRate}%</span>
                  <span className="metric-label">Success Rate</span>
                </div>
                <div className="metric-item">
                  <span className="metric-value">{automation.metrics.avgExecutionTime}</span>
                  <span className="metric-label">Avg Time</span>
                </div>
              </div>
            </div>
          )}

          <div className="detail-card">
            <h3>Schedule</h3>
            {automation.lastRun && (
              <div className="schedule-item">
                <span className="schedule-label">Last Run</span>
                <span className="schedule-value">{formatDate(automation.lastRun)}</span>
              </div>
            )}
            {automation.nextRun && (
              <div className="schedule-item">
                <span className="schedule-label">Next Run</span>
                <span className="schedule-value">{formatDate(automation.nextRun)}</span>
              </div>
            )}
          </div>

          <div className="detail-card">
            <h3>Metadata</h3>
            <div className="metadata-item">
              <span className="metadata-label">Category</span>
              <span className="metadata-value">{automation.category}</span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">Created</span>
              <span className="metadata-value">{formatDate(automation.createdAt)}</span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">Last Updated</span>
              <span className="metadata-value">{formatDate(automation.updatedAt)}</span>
            </div>
          </div>

          <div className="action-buttons">
            {automation.status === 'active' && (
              <button className="action-button pause-button">
                ⏸ Pause Automation
              </button>
            )}
            {automation.status === 'paused' && (
              <button className="action-button resume-button">
                ▶️ Resume Automation
              </button>
            )}
            <button className="action-button run-button">
              🚀 Run Now
            </button>
            <button className="action-button logs-button">
              📋 View Full Logs
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationDetail;
